import { defineComponent as _defineComponent } from 'vue'
import { computed, watch, reactive, ref, nextTick } from 'vue';
import CButton from '@/components/shared/CButton';
import type { CanvasEx } from '@/typings/canvas';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import DraggableList from '@/components/DesignStudio/DraggableList.vue';
import MapStyleListItem from '@/components/DesignStudio/MapStyleProperties/components/MapStyleListItem.vue';
import CustomMapStyleModal from '@/components/DesignStudio/MapStyleProperties/components/CustomMapStyleModal.vue';
import MapStyleCreateModal from '@/components/DesignStudio/MapStyleProperties/components/MapStyleCreateModal.vue';
import { MAP_STYLE_PRESETS } from '@/components/DesignStudio/MapStyleProperties/presets';

interface Props {
    canvas: CanvasEx;
    imageElement: ImagePlaceholderElement;
    editMode?: boolean;
}
interface Emits {
    (event: 'update-map', object: ImagePlaceholderElement): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MapStyleProperties',
  props: {
    canvas: null,
    imageElement: null,
    editMode: { type: Boolean }
  },
  emits: ["update-map"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'update-map', object: ImagePlaceholderElement): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    canvas: CanvasEx;
    imageElement: ImagePlaceholderElement;
    editMode?: boolean;
};

const DEFAULT_MAP_CONFIG = {
    hasPin: false,
    latitude: 17.9,
    longitude: -37.34,
    zoom: 1.077,
    availableStyles: [MAP_STYLE_PRESETS[0].value],
    defaultStyle: MAP_STYLE_PRESETS[0].value
};





const model = reactive({
    mapConfig: props.imageElement?.mapConfig || { ...DEFAULT_MAP_CONFIG }
});

const isAddModalOpen = ref(false);
const isEditModalOpen = ref(false);
const editingIndex = ref<number | undefined>();
const editingStyleId = ref('');

const isDisabled = computed(() => props.imageElement?.locked);
const hasMapStyles = computed(() => model.mapConfig?.availableStyles?.length > 0);
const availableStyleItems = computed(() =>
    (model.mapConfig?.availableStyles || []).map((style: string) => ({ styleId: style }))
);

const updateMapStyles = () => {
    if (!props.imageElement || isDisabled.value) return;

    props.imageElement.set('mapConfig', { ...model.mapConfig });

    if (props.editMode) {
        props.imageElement.setEditedProp('mapConfig');
    }

    props.canvas.requestRenderAll();
    emit('update-map', props.imageElement);
};
const openAddMapStyleModal = () => {
    isAddModalOpen.value = true;
};

const removeMapStyle = (mapStyle: string) => {
    if (!model.mapConfig || isDisabled.value) return;

    const index = model.mapConfig?.availableStyles?.findIndex((style) => style === mapStyle);

    if (index !== -1) {
        const isDefaultStyle = isDefault(index);
        model.mapConfig.availableStyles.splice(index, 1);
        if (isDefaultStyle) {
            model.mapConfig.defaultStyle = model.mapConfig.availableStyles[0] || '';
        }
        updateMapStyles();
    }
};

const updateMapStyle = async (data: { id: number; value: string }) => {
    if (!model.mapConfig || isDisabled.value) return;
    const { id, value } = data;

    const exist = model.mapConfig.availableStyles[id];

    // Validate that the index exists and the provided value is not empty
    if (typeof exist === 'undefined' || exist === null) return;
    if (!value) return;

    const isDefaultStyle = isDefault(id);
    model.mapConfig.availableStyles[id] = value;
    model.mapConfig.availableStyles = [...new Set(model.mapConfig.availableStyles)];

    if (isDefaultStyle) {
        model.mapConfig.defaultStyle = value;
    }

    updateMapStyles();
};

const isDefault = (index: number) => {
    return model.mapConfig.availableStyles[index] === model.mapConfig.defaultStyle;
};

const isCustomStyle = (style: string) => {
    return style === 'username/style_id';
};

const createMapStyle = async (value: string) => {
    if (!model.mapConfig || isDisabled.value) return;

    if (!value) return;

    model.mapConfig.availableStyles.push(value);
    model.mapConfig.availableStyles = [...new Set(model.mapConfig.availableStyles)];

    updateMapStyles();
    await nextTick();
    const isCustom = isCustomStyle(value);
    if (isCustom) {
        handleEditStyle(model.mapConfig.availableStyles.length - 1);
    }
};

const handleReorder = (items: Record<string, unknown>[]) => {
    if (!model.mapConfig || isDisabled.value) return;

    model.mapConfig.availableStyles = items.map((item) => item.styleId as string);
    updateMapStyles();
};

const handleDragEnd = () => {
    if (!model.mapConfig || isDisabled.value) return;
    updateMapStyles();
};

const handleEditStyle = (id: number) => {
    editingIndex.value = id;
    editingStyleId.value = model.mapConfig.availableStyles[id] || '';
    isEditModalOpen.value = true;
};

const handleSetDefault = (id: number) => {
    if (!model.mapConfig || isDisabled.value) return;

    model.mapConfig.defaultStyle = model.mapConfig.availableStyles[id];
    updateMapStyles();
};

watch(
    () => props.imageElement?.mapConfig,
    () => {
        if (props.imageElement?.mapConfig) {
            model.mapConfig = props.imageElement.mapConfig;
        } else {
            model.mapConfig = { ...DEFAULT_MAP_CONFIG };
        }
    },
    { deep: true }
);

return { __sfc: true,DEFAULT_MAP_CONFIG, emit, props, model, isAddModalOpen, isEditModalOpen, editingIndex, editingStyleId, isDisabled, hasMapStyles, availableStyleItems, updateMapStyles, openAddMapStyleModal, removeMapStyle, updateMapStyle, isDefault, isCustomStyle, createMapStyle, handleReorder, handleDragEnd, handleEditStyle, handleSetDefault, CButton, DraggableList, MapStyleListItem, CustomMapStyleModal, MapStyleCreateModal }
}

})